import { html } from '@shared/utils/rendering/html';
import { useMemo } from 'preact/hooks';
import { BarChart } from '@shared/components/charts';
import { CHART_COLORS } from '@shared/constants/chart-palette';
import { ErrorBoundary } from '@core/components/shared/error-boundary';
import { buildChartTooltip } from '@core/utils/helpers/chart-tooltip-builder';
import './principles-chart.scss';

// Keep same per-bar slot as the components chart (900px / 26 bars ≈ 35px)
const BAR_SLOT_PX = 35;
const CHART_PADDING_PX = 80;

export const PrinciplesChart = ({
    principleRatings = [],
    isLoading = false,
    selectedPrinciple = null,
}) => {
    const displayedRatings = useMemo(
        () =>
            selectedPrinciple
                ? principleRatings.filter(
                      (pr) => pr.principleNumber?.toString() === selectedPrinciple
                  )
                : principleRatings,
        [principleRatings, selectedPrinciple]
    );

    const chartData = useMemo(() => {
        if (!displayedRatings || displayedRatings.length === 0) return null;

        const sortedRatings = [...displayedRatings].sort(
            (a, b) => (a.principleNumber ?? 0) - (b.principleNumber ?? 0)
        );
        const categories = sortedRatings.map((pr) => {
            const pct = pr.totalQuestions
                ? Math.round((pr.completedQuestions / pr.totalQuestions) * 100)
                : 0;
            return [pr.principleName || `Principle ${pr.principleNumber}`, `${pct}% Completed`];
        });
        // Method A ratings are already 1dp - display them that way (2.3, not 2.30).
        const values = sortedRatings.map((pr) =>
            pr.averageRating ? parseFloat(pr.averageRating.toFixed(1)) : 0
        );

        return { categories, datasets: [{ name: 'Average Rating', values }] };
    }, [displayedRatings]);

    const chartConfig = useMemo(() => {
        const numBars = displayedRatings.length || 11;
        const chartHeight = Math.max(200, numBars * BAR_SLOT_PX + CHART_PADDING_PX);

        return {
            horizontal: true,
            stacked: false,
            borderRadius: 4,
            columnWidth: '70%',
            showDataLabels: false,
            overrides: {
                colors: [CHART_COLORS.primary],
                chart: {
                    height: chartHeight,
                    toolbar: { show: false },
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        borderRadius: 5,
                        borderRadiusApplication: 'end',
                        barHeight: '55%',
                    },
                },
                stroke: { show: true, width: 2, colors: ['transparent'] },
                xaxis: {
                    min: 0,
                    max: 4.5,
                    tickAmount: 9,
                    labels: { formatter: (val) => val?.toFixed(1) },
                },
                yaxis: {
                    labels: { style: { fontSize: '13px' }, maxWidth: 400 },
                },
                dataLabels: { enabled: false },
                tooltip: {
                    enabled: true,
                    custom: ({ series, seriesIndex, dataPointIndex }) => {
                        const sortedRatings = [...displayedRatings].sort(
                            (a, b) => (a.principleNumber ?? 0) - (b.principleNumber ?? 0)
                        );
                        const rating = sortedRatings[dataPointIndex];
                        if (!rating) return '';
                        const avgRating = series[seriesIndex][dataPointIndex]?.toFixed(1) || '0.0';
                        const pct = rating.totalQuestions
                            ? Math.round((rating.completedQuestions / rating.totalQuestions) * 100)
                            : 0;
                        return buildChartTooltip({
                            header: rating.principleName,
                            metrics: [
                                { label: 'Average', value: avgRating },
                                { label: 'Completed', value: `${pct}%` },
                            ],
                        });
                    },
                },
            },
        };
    }, [displayedRatings]);

    return html`
        <${ErrorBoundary} fallback=${html`<p>Unable to display principles chart.</p>`}>
            <${BarChart}
                data=${chartData}
                config=${chartConfig}
                isLoading=${isLoading}
                noDataMessage="No results to show — no principles have been assessed yet"
                css="sarb-principles-chart"
            />
        <//>
    `;
};
